import { ChartAssetData, isSinglePrice } from '@toggle/chart';
import { Button, Icon } from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import {
  ChartNavigateConfigParams,
  useChartNavigate,
} from '~/hooks/use-chart-navigate/useChartNavigate';
import { ChartSearchParamsAssetValue } from '~/utils/chart-search-utils/chart-search-utils';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';

import styles from './ChartMessageHeader.module.scss';

export interface ChartMessageHeaderProps {
  chartData: ChartAssetData[];
  showChartArea: boolean;
  chartConfig?: ChartNavigateConfigParams;
}

export const ChartMessageHeader = ({
  chartData,
  showChartArea,
  chartConfig,
}: ChartMessageHeaderProps) => {
  const { colors } = useChartTheme();
  const chartNavigate = useChartNavigate();
  const { t } = useTranslation(['chart']);

  if (!showChartArea) {
    return null;
  }

  const navigateToChart = () => {
    const assets: ChartSearchParamsAssetValue[] = chartData.map(asset => [
      asset.entity.tag,
      asset.entity.default_snake,
    ]);

    chartNavigate(assets, chartConfig);
  };

  return (
    <div className={styles.container} data-testid="chart-message-header">
      <div className={styles.labelRoot}>
        {chartData.map((c, i) => {
          const { entity, snakeMeta } = c;
          return (
            <TooltipContainer key={i} label={entity.name} inPortal>
              <div className={styles.labelContainer}>
                <Icon
                  iconName="Dot"
                  size={20}
                  fillColor={colors.getThemeColor(c.lineColorToken)}
                />
                <p className={styles.label}>
                  {!isSinglePrice(entity)
                    ? entity.formattedTicker
                    : `${entity.formattedTicker} ${snakeMeta?.name.english ?? ''
                    }`}
                </p>
              </div>
            </TooltipContainer>
          );
        })}
      </div>
      <div>
        <Button
          variant="secondary"
          iconName="Expand"
          label={t('chart:fullscreen')}
          data-testid="open-full-chart"
          onClick={navigateToChart}
        />
      </div>
    </div>
  );
};
